import { FC, useCallback, useContext, useReducer, useRef } from "react";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import ExceptionsService from "@app/exceptions/exceptionsService";
import { CreateSpecialtyGroupException, SupplementaryDocument } from "@app/exceptions/interfaces";
import {
  SpecialtyGroupExceptionGrade,
  SpecialtyGroupExceptionReviewStatus,
  SpecialtyGroupExceptionStatus,
} from "@app/exceptions/enums";
import { toast } from "react-toastify";
import DragAndDropMultiFileUploader from "@app/shared/DragAndDropMultiFileUploader";
import {
  DragAndDropMultiFileUploaderContext,
  DragAndDropMultiFileUploaderContextProvider,
} from "@app/shared/DragAndDropMultiFileUploaderContext";
import { authenticatedDownload } from "@app/shared/AuthenticatedDownload";
import {
  EditExceptionFormActionType,
  editExceptionFormReducer,
  EditExceptionForm,
  normalizeSpaces,
} from "@app/exceptions/editExceptionReducer";
import { ExceptionGapRow } from "@app/compliance-connect/network-submission/interfaces";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { QESTextArea } from "@app/shared/qes-standards/forms/TextArea";
import { NetworkSubmissionContext } from "@app/compliance-connect/network-submission/NetworkSubmissionContext";
import { NetworkSubmissionWizardAction } from "@app/compliance-connect/network-submission/networkSubmissionWizardActions";

export interface NetworkSubmissionExceptionFlyoutProps {
  row: ExceptionGapRow;
}

const INITIAL_FORM_STATE: EditExceptionForm = {
  comment: "",
  endDate: "",
  excludeFromCCIIOJustification: false,
  grade: SpecialtyGroupExceptionGrade.None,
  narrative: "",
  originalComment: "",
  originalEndDate: "",
  originalExcludeFromCCIIOJustification: false,
  originalGrade: SpecialtyGroupExceptionGrade.None,
  originalNarrative: "",
  originalPreviouslyApproved: false,
  originalReviewStatus: SpecialtyGroupExceptionReviewStatus.None,
  originalStartDate: "",
  originalStatus: SpecialtyGroupExceptionStatus.Pending,
  originalUploadedDocuments: [],
  previouslyApproved: false,
  reviewStatus: SpecialtyGroupExceptionReviewStatus.None,
  startDate: "",
  status: SpecialtyGroupExceptionStatus.Pending,
  uploadedDocuments: [],
  validEndDate: true,
  validStartDate: true,
};

const UPLOAD_API_KEY = "document";

const WrappedNetworkSubmissionExceptionFlyout: FC<NetworkSubmissionExceptionFlyoutProps> = ({ row }) => {
  const flyoutContext = useContext(FlyoutContext);
  const multiFileUploaderContext = useContext(DragAndDropMultiFileUploaderContext);
  const { dispatch } = useContext(NetworkSubmissionContext);
  const exceptionsService = useRef(new ExceptionsService()).current;
  const [formState, formDispatch] = useReducer(editExceptionFormReducer, INITIAL_FORM_STATE);

  const handleSubmit = (): void => {
    const exception: CreateSpecialtyGroupException = {
      comment: formState.comment,
      exceptionNarrative: formState.narrative,
      excludeFromCCIIOJustification: false,
      grade: formState.grade,
      previouslyApproved: formState.previouslyApproved,
      regionSetId: parseInt(row.countyId, 10),
      specialtyGroupCode: row.specialtyGroupCode,
      specialtyGroupExceptionStatus: formState.status,
      startDate: formState.startDate ? new Date(formState.startDate) : undefined,
      supplementaryDocuments: formState.uploadedDocuments,
    };

    dispatch({
      payload: {
        exception,
        key: `${row.countyId}:${row.specialtyGroupCode}`,
      },
      type: NetworkSubmissionWizardAction.AddPendingException,
    });

    flyoutContext.handleCloseFlyout();

    toast(<span>Exception added. It will be submitted with the network submission.</span>, {
      containerId: "z-index-9999",
      type: "success",
    });
  };

  const handleFileUpload = useCallback(async (file: File) => {
    const formData = new FormData();
    formData.append(UPLOAD_API_KEY, file);
    const metadataId = await exceptionsService.uploadSupplementaryDocument(formData);
    const fileInfo = formData.get(UPLOAD_API_KEY);

    if (!!metadataId && fileInfo instanceof File) {
      const supplementaryDocument: SupplementaryDocument = {
          documentName: fileInfo.name ?? "",
        supplementaryDocumentMetadataId: metadataId,
      };
      formDispatch({ payload: [supplementaryDocument], type: EditExceptionFormActionType.AddUploadedDocuments });
      multiFileUploaderContext.setUploadedFiles(prevFiles => new Map(prevFiles).set(metadataId, file));
    }
  }, [exceptionsService, multiFileUploaderContext]);

  const handleDownload = (metadataId: number): void => {
    const link = metadataId > 0 ? exceptionsService.downloadSupplementaryDocument(metadataId) : "#";
    authenticatedDownload(link);
  };

  return (
    <>
      <div className="row">
        <div className="col-10 mb-3">
          <div data-testid="ns-exception-flyout__intro-message">
            {`Complete the Specialty Exception process for ${row.specialtyGroupDisplayName} in ${row.countyName}.`}
          </div>

          <QESDateInput
            containerClassName="mt-3 col-5 p-0"
            isValid={formState.validStartDate}
            label="Start Date"
            onChange={(e) => formDispatch({ payload: e.target.value, type: EditExceptionFormActionType.StartDate })}
            testId="ns-exception-flyout__start-date"
            unrequiredValidation={true}
            value={formState.startDate}
          />

          <QESCheckbox
            checked={formState.previouslyApproved}
            containerClassName="mt-3"
            id="ns-exception-flyout__previously-approved"
            label="Previously Approved"
            onChange={(e) => formDispatch({
              payload: (e.target as HTMLInputElement).checked,
              type: EditExceptionFormActionType.PreviouslyApproved,
            })}
            testId="ns-exception-flyout__previously-approved"
          />

          <QESTextArea
            containerClassName="mt-3"
            id="ns-exception-flyout__comment-input"
            label="Comments"
            maxLength={4000}
            onBlur={() => formDispatch({ payload: normalizeSpaces(formState.comment), type: EditExceptionFormActionType.Comment })}
            onChange={(e) => formDispatch({ payload: e.target.value, type: EditExceptionFormActionType.Comment })}
            rows={4}
            testId="ns-exception-flyout__comment-input"
            value={formState.comment}
          />

          <QESTextArea
            containerClassName="mt-3"
            id="ns-exception-flyout__narrative-input"
            label="Exception Narrative"
            maxLength={4000}
            onBlur={() => formDispatch({ payload: normalizeSpaces(formState.narrative), type: EditExceptionFormActionType.Narrative })}
            onChange={(e) => formDispatch({ payload: e.target.value, type: EditExceptionFormActionType.Narrative })}
            rows={8}
            testId="ns-exception-flyout__narrative-input"
            value={formState.narrative}
          />

          <DragAndDropMultiFileUploader
            apiKeyName={UPLOAD_API_KEY}
            filetype=".doc,.docx,.png,.jpg,.jpeg,.pdf,.xls,.xlsx,.csv"
            handleDownloadLink={handleDownload}
            onMultipleUpload={handleFileUpload}
            onRemove={(key) => formDispatch({ payload: key, type: EditExceptionFormActionType.RemoveUploadedDocumentByKey })}
            small={true}
            testId="ns-exception-flyout__upload-docs"
            title="Attach Supplemental Documents. Attachments must be in .doc, .docx, .png, .jpg, .pdf, .xls, .xlsx, or .csv format."
          />
        </div>
      </div>

      <FlyoutFooter
        submitButton={
          <QESButton
            onClick={handleSubmit}
            qesTestId="ns-exception-flyout__submit"
            qesType="primary"
          >
            Submit
          </QESButton>
        }
      />
    </>
  );
};

const NetworkSubmissionExceptionFlyout: FC<NetworkSubmissionExceptionFlyoutProps> = ({ row }) => (
  <DragAndDropMultiFileUploaderContextProvider>
    <WrappedNetworkSubmissionExceptionFlyout row={row} />
  </DragAndDropMultiFileUploaderContextProvider>
);

export default NetworkSubmissionExceptionFlyout;
